import { ErrorOutlineRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import OTPInput from "../components/elements/OTPInput";
import PhoneInput from "../components/elements/PhoneInput";
import { AMERICAS_COUNTRIES } from "../config/config";
import { useLogin } from "./hooks/useLogin";

const GREEN = "#00C853";
const DARK = "#0D1420";

export default function LoginPage() {
  const [country, setCountry] = useState(
    AMERICAS_COUNTRIES.find((c) => c.code === "+57")
  );

  const formRef = useRef<HTMLFormElement>(null);

  const {
    phone,
    setPhone,
    code,
    setCode,
    step,
    error,
    isSubmitting,
    isSubmittingWhatsapp,
    isLogged,
    isPhoneValid,
    isCodeValid,
    handleSendCodeBySms,
    handleVerifyCodeSms,
    handleSendCodeByWhatsapp,
    handleVerifyCodeByWhatsapp,
    recaptchaContainerRef,
  } = useLogin({ country });

  if (isLogged) {
    window.location.href = "/dashboard";
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400, p: 3 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&display=swap');`}</style>
      <div id="recaptcha-container" ref={recaptchaContainerRef}></div>

      <Box sx={{ width: "100%", maxWidth: 380 }}>

        {/* Logo centrado */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img src="/assets/icons/logo.png" alt="PrestiYA" style={{ height: 90 }} />
        </Box>

        {/* Título */}
        <Typography sx={{ fontFamily: "Outfit", fontWeight: 800, fontSize: 26, color: DARK, textAlign: "center", mb: 0.5 }}>
          {step === "input" ? "Bienvenido a Prestiya" : "Verificá tu código"}
        </Typography>
        <Typography sx={{ fontFamily: "Outfit", fontSize: 14, color: "#667788", textAlign: "center", mb: 3, lineHeight: 1.5 }}>
          {step === "input"
            ? "Ingresá tu número para registrarte o iniciar sesión."
            : "Ingresá el código de 6 dígitos que te enviamos."}
        </Typography>

        {/* Step: input teléfono */}
        {step === "input" && (
          <form onSubmit={handleSendCodeBySms}>
            <PhoneInput
              phone={phone}
              setPhone={setPhone}
              country={country}
              setCountry={setCountry}
            />
            <Stack direction="column" spacing={2} sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={!isPhoneValid || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={18} sx={{ color: "white" }} /> : undefined}
                sx={{
                  bgcolor: DARK,
                  color: "white",
                  fontFamily: "Outfit",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: "50px",
                  py: 1.5,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#1a2840", boxShadow: "none" },
                  "&:disabled": { bgcolor: "#cccccc", color: "white" },
                }}
              >
                Enviar por SMS
              </Button>
              <Button
                fullWidth
                variant="contained"
                disabled={!isPhoneValid || isSubmittingWhatsapp}
                startIcon={
                  isSubmittingWhatsapp ? (
                    <CircularProgress size={18} sx={{ color: "white" }} />
                  ) : (
                    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="" style={{ width: 20, height: 20 }} />
                  )
                }
                onClick={(e) => { e.preventDefault(); handleSendCodeByWhatsapp(e); }}
                sx={{
                  bgcolor: GREEN,
                  color: "white",
                  fontFamily: "Outfit",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: "50px",
                  py: 1.5,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#00b048", boxShadow: "none" },
                  "&:disabled": { bgcolor: "#cccccc", color: "white" },
                }}
              >
                Enviar por WhatsApp
              </Button>
            </Stack>
          </form>
        )}

        {/* Step: código SMS */}
        {step === "code" && (
          <form ref={formRef} onSubmit={handleVerifyCodeSms}>
            <OTPInput length={6} value={code} onChange={setCode} onComplete={() => { setTimeout(() => { if (formRef.current) formRef.current.requestSubmit(); }, 50); }} />
            <Button
              fullWidth variant="contained" type="submit"
              disabled={!isCodeValid || isSubmitting}
              sx={{ mt: 3, bgcolor: DARK, color: "white", fontFamily: "Outfit", fontWeight: 700, fontSize: 15, borderRadius: "50px", py: 1.5, textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#1a2840" } }}
            >
              {isSubmitting ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Verificar código"}
            </Button>
          </form>
        )}

        {/* Step: código WhatsApp */}
        {step === "codeWhatsapp" && (
          <form ref={formRef} onSubmit={handleVerifyCodeByWhatsapp}>
            <OTPInput length={6} value={code} onChange={setCode} onComplete={() => { setTimeout(() => { if (formRef.current) formRef.current.requestSubmit(); }, 50); }} />
            <Button
              fullWidth variant="contained" type="submit"
              disabled={!isCodeValid || isSubmitting}
              sx={{ mt: 3, bgcolor: GREEN, color: "white", fontFamily: "Outfit", fontWeight: 700, fontSize: 15, borderRadius: "50px", py: 1.5, textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}
            >
              {isSubmitting ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Verificar código"}
            </Button>
          </form>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 3, fontFamily: "Outfit" }} iconMapping={{ error: <ErrorOutlineRounded sx={{ fontSize: 24 }} /> }}>
            {error}
          </Alert>
        )}

        <Typography sx={{ fontFamily: "Outfit", fontSize: 12, color: "#aabbcc", textAlign: "center", mt: 3 }}>
          Al continuar aceptás nuestras{" "}
          <a href="/privacidad" style={{ color: GREEN }}>políticas de privacidad</a>
        </Typography>
      </Box>
    </Box>
  );
}
